import React, { useState } from 'react'
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import SCREEN_IDS from '../../utils/screenIds'

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api'

const InventoryUpload = () => {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedRows, setExpandedRows] = useState({})

  const reset = () => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setExpandedRows({})
  }

  const handleFileChange = (e) => {
    const f = e.target.files[0]
    if (f) { setFile(f); setPreview(null); setResult(null); setError(null) }
  }

  const handlePreview = async () => {
    if (!file) return
    setLoading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE_URL}/inventory-upload/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error)
      setPreview(data.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCommit = async () => {
    if (!file || !preview) return
    if (!window.confirm(`Insert ${preview.toInsert} items into inventory. Continue?`)) return
    setLoading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE_URL}/inventory-upload/commit`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error)
      setResult(data.data)
      setPreview(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleRow = (i) => setExpandedRows(prev => ({ ...prev, [i]: !prev[i] }))

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <PageIdentifier pageId={SCREEN_IDS.UTILITIES.INVENTORY_UPLOAD} pageName="Inventory Upload" />
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-1">
          <FileSpreadsheet className="text-amber-500" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Inventory Upload</h2>
        </div>
        <p className="text-sm text-gray-500">
          Upload an Excel file (.xlsx) with columns: <span className="font-medium">Vendor, Tag, Plus Date, Purity, Design No, Gr Wt, Net Wt</span> + stone code columns
        </p>
      </div>

      {/* Upload Box */}
      {!result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <label className="block">
            <div className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
              ${file ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'}`}>
              <Upload className="mx-auto mb-3 text-gray-400" size={32} />
              {file
                ? <p className="font-medium text-amber-700">{file.name}</p>
                : <p className="text-gray-500">Click to select an Excel file</p>
              }
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
            </div>
          </label>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handlePreview}
              disabled={!file || loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
            >
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FileSpreadsheet size={16} />}
              {loading ? 'Processing...' : 'Preview'}
            </button>
            {file && (
              <button onClick={reset} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg">
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && !result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Rows', value: preview.totalRows, color: 'text-gray-800' },
              { label: 'To Insert', value: preview.toInsert, color: 'text-green-600' },
              { label: 'Duplicates', value: preview.duplicates.length, color: 'text-amber-600' },
              { label: 'Errors', value: preview.errors.length, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Duplicates */}
          {preview.duplicates.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-700 mb-1">Already in inventory (will be skipped):</p>
              <p className="text-sm text-amber-600">{preview.duplicates.join(', ')}</p>
            </div>
          )}

          {/* Errors */}
          {preview.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-medium text-red-700 mb-2">Rows with errors (will not be inserted):</p>
              <ul className="text-sm text-red-600 space-y-1">
                {preview.errors.map((e, i) => (
                  <li key={i}>• <strong>{e.tag}</strong>: {e.error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Items table */}
          {preview.preview.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Items to be inserted ({preview.toInsert})</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Tag / Item Code', 'Category', 'Vendor', 'Purity', 'Gr Wt', 'Net Wt', 'Description', 'Stones', ''].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.preview.map((item, i) => (
                      <React.Fragment key={i}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-800">{item.tag}</td>
                          <td className="px-3 py-2 text-gray-600">{item.category}</td>
                          <td className="px-3 py-2 text-gray-600">{item.vendor}</td>
                          <td className="px-3 py-2 text-gray-600">{item.purity}</td>
                          <td className="px-3 py-2 text-gray-600">{item.grossWeight}</td>
                          <td className="px-3 py-2 text-gray-600">{item.netWeight}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs max-w-36 truncate">{item.description}</td>
                          <td className="px-3 py-2 text-gray-500">{item.stonesCount} stone{item.stonesCount !== 1 ? 's' : ''}</td>
                          <td className="px-3 py-2">
                            {item.stonesCount > 0 && (
                              <button onClick={() => toggleRow(i)} className="text-gray-400 hover:text-gray-600">
                                {expandedRows[i] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRows[i] && item.stonesCount > 0 && (
                          <tr className="bg-amber-50">
                            <td colSpan={9} className="px-6 py-2 text-xs text-amber-700">
                              {item.stones}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleCommit}
              disabled={loading || preview.toInsert === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
            >
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={16} />}
              {loading ? 'Inserting...' : `Insert ${preview.toInsert} Items`}
            </button>
            <button onClick={reset} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-500" size={28} />
            <h3 className="text-lg font-semibold text-gray-800">Upload Complete</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Inserted', value: result.inserted, color: 'text-green-600' },
              { label: 'Skipped (duplicates)', value: result.skipped, color: 'text-amber-600' },
              { label: 'Total In Stock', value: result.totalInStock, color: 'text-blue-600' },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {result.skippedList.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <strong>Skipped:</strong> {result.skippedList.join(', ')}
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
              <ul className="text-sm text-red-600 space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i}>• <strong>{e.tag}</strong>: {e.error}</li>
                ))}
              </ul>
            </div>
          )}

          <button onClick={reset} className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors">
            <Upload size={16} /> Upload Another File
          </button>
        </div>
      )}
    </div>
  )
}

export default InventoryUpload
